import * as React from "react";
import { useState } from "react";
import SettingLogo from "./settings/SettingLogo";
import SettingPrimaryColor from "./settings/SettingPrimaryColor";
import { BrandingModel, PracticeLevelBrandingModel } from "./settings/BrandingModels";
import PracticeCustomisationOverride from "./settings/PracticeCustomisationOverride";
import SettingsForm from "./settings/SettingsForm";
import sfetch from "./commons/utils/super-fetch";
import { AvailableEntitiesSettings } from "./commons/model/EntitySettings";
import { createRoot } from "react-dom/client";

export interface SettingsBrandingProps {
  organisationalBrandingModel: BrandingModel;
  practiceLevelBrandingModel?: PracticeLevelBrandingModel;
}

const SettingsBranding = ({
  settingsBranding,
  entities,
  isDsoEmployee,
}: {
  settingsBranding: SettingsBrandingProps;
  entities: AvailableEntitiesSettings;
  isDsoEmployee: boolean;
}) => {
  const [practiceOverride, setPracticeOverride] = useState<boolean>(
    settingsBranding.practiceLevelBrandingModel?.practiceBrandingOverride || false
  );
  const [logo, setLogo] = useState<Blob | null | undefined>(undefined);
  const [primaryColor, setPrimaryColor] = useState<string>(
    settingsBranding.organisationalBrandingModel.primaryColor.primaryColor
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const savePracticeOverride = async () => {
    if (
      settingsBranding.practiceLevelBrandingModel &&
      practiceOverride !== settingsBranding.practiceLevelBrandingModel.practiceBrandingOverride
    ) {
      const body = {
        enabled: practiceOverride,
        entitySettingsId: { value: entities.selectedEntity.entitySettingsId.value },
      };
      return sfetch(settingsBranding.practiceLevelBrandingModel.updateUrl, {
        body: JSON.stringify(body),
        method: "PUT",
        headers: { "Content-Type": "application/json" },
      });
    }
  };

  const savePrimaryColor = async () => {
    if (
      primaryColor &&
      primaryColor !== settingsBranding.organisationalBrandingModel.primaryColor.primaryColor
    ) {
      return sfetch(settingsBranding.organisationalBrandingModel.primaryColor.changeUrl, {
        method: "POST",
        body: primaryColor,
      });
    }
  };

  const saveLogo = async () => {
    if (logo === null && !settingsBranding.organisationalBrandingModel.logo.isDefault) {
      return sfetch(settingsBranding.organisationalBrandingModel.logo.deleteUrl, {
        method: "DELETE",
      });
    } else if (logo) {
      const body = new FormData();
      body.append("file", logo);
      return sfetch(settingsBranding.organisationalBrandingModel.logo.changeUrl, {
        body,
        method: "PUT",
      });
    }
  };

  const hasChanges = (): boolean => {
    return (
      logo !== undefined ||
      (primaryColor !== settingsBranding.organisationalBrandingModel.primaryColor.primaryColor &&
        isPrimaryColorCorrect()) ||
      (settingsBranding.practiceLevelBrandingModel &&
        practiceOverride != settingsBranding.practiceLevelBrandingModel.practiceBrandingOverride)
    );
  };

  const isPrimaryColorCorrect = (): boolean => {
    const colorPattern = /^#[0-9a-fA-F]{6}$/i;
    return primaryColor.match(colorPattern) !== null;
  };

  const saveBranding = () => {
    return Promise.all([saveLogo(), savePrimaryColor(), savePracticeOverride()])
      .then(() => document.location.reload())
      .catch((e) => {
        setErrorMessage(i18n["Global.Status.technical_error"]);
        throw e;
      });
  };

  const cancelBrandingChanges = () => {
    setLogo(undefined);
    setPrimaryColor(settingsBranding.organisationalBrandingModel.primaryColor.primaryColor);
    setPracticeOverride(settingsBranding.practiceLevelBrandingModel.practiceBrandingOverride);
  };

  const organisation = () => {
    const brandingProps = settingsBranding.organisationalBrandingModel;
    const logoUrl =
      logo === undefined ? brandingProps.logo.logoUrl : logo && URL.createObjectURL(logo);
    return (
      brandingProps && (
        <div key="organisation-branding" className="v-flex with-gap">
          <SettingLogo
            logoUrl={logoUrl}
            isDefault={brandingProps.logo.isDefault && !logo}
            supportedTypes={brandingProps.logo.supportedTypes}
            maxSizeInBytes={brandingProps.logo.maxSizeInBytes}
            displayError={(errorMessage) => setErrorMessage(errorMessage)}
            onLogoChange={(blob) => {
              setLogo(blob);
              setErrorMessage(null);
            }}
            onLogoReset={() => setLogo(brandingProps.logo.isDefault ? undefined : null)}
          />
          <SettingPrimaryColor
            primaryColor={primaryColor}
            defaultColor={settingsBranding.organisationalBrandingModel.primaryColor.defaultColor}
            onColorChange={(brandingColor) => setPrimaryColor(brandingColor)}
          />
        </div>
      )
    );
  };

  const practice = () => {
    return (
      isDsoEmployee &&
      settingsBranding.practiceLevelBrandingModel && (
        <div key="practice-branding">
          <PracticeCustomisationOverride
            value={practiceOverride}
            onValueChange={(value) => setPracticeOverride(value)}
          />
        </div>
      )
    );
  };

  const errorPanel = () => {
    return (
      <React.Fragment key="branding-error">
        {errorMessage && <div className="message danger">{errorMessage}</div>}
      </React.Fragment>
    );
  };

  const buildDisplay = () => [practice(), organisation(), errorPanel()];

  return (
    <SettingsForm
      title={i18n["Page.Settings.Branding.Title"]}
      hasChanges={hasChanges()}
      onCancel={cancelBrandingChanges}
      onSave={saveBranding}
      id={"virtual-consultation-customisation-branding"}
    >
      <>{buildDisplay()}</>
    </SettingsForm>
  );
};

export default SettingsBranding;

SettingsBranding.loadComponent = (elementId: string, props) => {
  const container = document.getElementById(elementId);
  const root = createRoot(container!);
  root.render(<SettingsBranding {...props} />);
};
